import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, Zap, Calendar, Download, AlertTriangle } from 'lucide-react';
import { format, addMinutes } from 'date-fns';
import { apiService } from '../services/apiService';

function InformeCortes() {
    const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [cortes, setCortes] = useState({ linea: [], comunicacion: [], micro: [] });
    const [resumen, setResumen] = useState(null);
    const [loading, setLoading] = useState(false);

    const UMBRAL_TENSION_CORTE = 5000; // 5kV - Corte de línea
    const UMBRAL_TENSION_MICRO = 20000; // 20kV - Microcorte
    const UMBRAL_GAP_COMUNICACION = 45000; // 45 segundos - Corte de comunicación

    useEffect(() => {
        loadInformeCortes(selectedDate);
    }, [selectedDate]);

    const loadInformeCortes = async (fecha) => {
        setLoading(true);
        try {
            // Solicitar suficientes datos para cubrir todo el día (aprox 18000 registros/día a 5s)
            // Límite 50000 y 36 horas atrás cubre el día completo con margen
            const data = await apiService.getMediciones('planta', 50000, 36);

            // Filtrar por fecha (el día operativo empieza a las 00:15)
            const fechaInicio = new Date(fecha + 'T00:15:00');
            const fechaFin = new Date(fecha + 'T00:15:00');
            fechaFin.setDate(fechaFin.getDate() + 1);

            const medicionesDia = data.filter(item => {
                const itemDate = new Date(item.timestamp);
                return itemDate >= fechaInicio && itemDate < fechaFin;
            }).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

            // Detectar cortes
            const cortesLinea = [];
            const cortesComunicacion = [];
            const microCortes = [];
            let enCorteLinea = false;
            let inicioCorteLinea = null;

            // Detectar gap de comunicación INICIAL (desde 00:15 hasta el primer dato)
            if (medicionesDia.length > 0) {
                const primerDato = new Date(medicionesDia[0].timestamp);
                const gapInicial = primerDato - fechaInicio;
                if (gapInicial > UMBRAL_GAP_COMUNICACION) {
                    cortesComunicacion.push({
                        id: 1,
                        inicio: format(fechaInicio, 'HH:mm:ss'),
                        fin: format(primerDato, 'HH:mm:ss'),
                        duracion: Math.floor(gapInicial / 1000)
                    });
                }
            }

            for (let i = 0; i < medicionesDia.length; i++) {
                const m = medicionesDia[i];
                const tension = m.tension_vab || m.tension_l1 || 0;
                // Usar kwDesp como en App.jsx, o kw como fallback
                const potencia = parseFloat(m.kwDesp || m.kw || 0);

                // Detectar corte de línea
                // Solo si la tensión es baja Y la potencia es casi nula.
                // Si hay potencia (>50kW), es un error de lectura de tensión, no un corte real.
                if (tension < UMBRAL_TENSION_CORTE && potencia < 50) {
                    if (!enCorteLinea) {
                        enCorteLinea = true;
                        inicioCorteLinea = new Date(m.timestamp);
                    }
                } else {
                    if (enCorteLinea && inicioCorteLinea) {
                        const finCorte = new Date(m.timestamp);
                        const duracionMs = finCorte - inicioCorteLinea;
                        const duracionSeg = Math.floor(duracionMs / 1000);
                        // Solo contar cortes de línea mayores a 5 segundos (filtrar ruido)
                        if (duracionSeg >= 5) {
                            cortesLinea.push({
                                id: cortesLinea.length + 1,
                                inicio: format(inicioCorteLinea, 'HH:mm:ss'),
                                fin: format(finCorte, 'HH:mm:ss'),
                                duracion: duracionSeg,
                                tensionMin: tension
                            });
                        } else if (duracionSeg > 0) {
                            microCortes.push({
                                id: microCortes.length + 1,
                                inicio: format(inicioCorteLinea, 'HH:mm:ss'),
                                fin: format(finCorte, 'HH:mm:ss'),
                                duracion: duracionSeg
                            });
                        }
                        enCorteLinea = false;
                        inicioCorteLinea = null;
                    }

                    // Detección adicional de bajones breves (Microcortes)
                    if (tension < UMBRAL_TENSION_MICRO && tension >= UMBRAL_TENSION_CORTE) {
                        if (i > 0) {
                            const prevTension = medicionesDia[i - 1].tension_vab || medicionesDia[i - 1].tension_l1 || 0;
                            if (prevTension > 30000) {
                                microCortes.push({
                                    id: microCortes.length + 1,
                                    inicio: format(new Date(m.timestamp), 'HH:mm:ss'),
                                    fin: format(new Date(m.timestamp), 'HH:mm:ss'),
                                    duracion: 0,
                                    tipo: 'Bajón'
                                });
                            }
                        }
                    }
                }

                // Detectar corte de comunicación (gap entre registros consecutivos)
                if (i < medicionesDia.length - 1) {
                    const t1 = new Date(m.timestamp);
                    const t2 = new Date(medicionesDia[i + 1].timestamp);
                    const gap = t2 - t1;

                    if (gap > UMBRAL_GAP_COMUNICACION) {
                        cortesComunicacion.push({
                            id: cortesComunicacion.length + 1,
                            inicio: format(t1, 'HH:mm:ss'),
                            fin: format(t2, 'HH:mm:ss'),
                            duracion: Math.floor(gap / 1000)
                        });
                    }
                }
            }

            setCortes({ linea: cortesLinea, comunicacion: cortesComunicacion, micro: microCortes });

            // Resumen
            const totalTiempoLinea = cortesLinea.reduce((sum, c) => sum + c.duracion, 0);
            const totalTiempoCom = cortesComunicacion.reduce((sum, c) => sum + c.duracion, 0);

            setResumen({
                fecha,
                cortesLinea: cortesLinea.length,
                tiempoTotalLinea: totalTiempoLinea,
                microCortes: microCortes.length,
                cortesComunicacion: cortesComunicacion.length,
                tiempoTotalComunicacion: totalTiempoCom,
                disponibilidad: medicionesDia.length > 0
                    ? (100 - (totalTiempoCom / (24 * 3600) * 100)).toFixed(2)
                    : '0.00'
            });

        } catch (error) {
            console.error('Error cargando informe de cortes:', error);
        }
        setLoading(false);
    };

    const formatTiempo = (segundos) => {
        if (segundos < 60) return `${segundos}s`;
        if (segundos < 3600) {
            const mins = Math.floor(segundos / 60);
            const secs = segundos % 60;
            return `${mins}m ${secs}s`;
        }
        const hours = Math.floor(segundos / 3600);
        const mins = Math.floor((segundos % 3600) / 60);
        return `${hours}h ${mins}m`;
    };

    const exportarExcel = () => {
        let csv = 'INFORME DE CORTES - ' + selectedDate + '\n\n';

        csv += 'CORTES DE LÍNEA EXTERNA\n';
        csv += 'ID,Inicio,Fin,Duración (s)\n';
        cortes.linea.forEach(c => {
            csv += `${c.id},${c.inicio},${c.fin},${c.duracion}\n`;
        });

        csv += '\n\nCORTES DE COMUNICACIÓN\n';
        csv += 'ID,Inicio,Fin,Duración (s)\n';
        cortes.comunicacion.forEach(c => {
            csv += `${c.id},${c.inicio},${c.fin},${c.duracion}\n`;
        });

        csv += '\n\nRESUMEN\n';
        csv += `Total Cortes Línea,${resumen?.cortesLinea}\n`;
        csv += `Tiempo Total Línea,${resumen?.tiempoTotalLinea}s\n`;
        csv += `Total Microcortes,${resumen?.microCortes}\n`;
        csv += `Total Cortes Comunicación,${resumen?.cortesComunicacion}\n`;
        csv += `Tiempo Total Comunicación,${resumen?.tiempoTotalComunicacion}s\n`;
        csv += `Disponibilidad,${resumen?.disponibilidad}%\n`;

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `informe_cortes_${selectedDate}.csv`;
        link.click();
    };

    return (
        <div className="space-y-6">
            {/* Cabecera */}
            <div className="card">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center space-x-3">
                        <AlertTriangle className="w-8 h-8 text-red-600" />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Informe de Cortes</h2>
                            <p className="text-sm text-gray-500">Cortes de línea externa y comunicación</p>
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                            <Calendar className="w-5 h-5 text-gray-400" />
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                        </div>

                        <button
                            onClick={exportarExcel}
                            disabled={!resumen}
                            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                            <Download className="w-5 h-5" />
                            <span>Exportar</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Resumen */}
            {resumen && (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <div className={`card ${resumen.cortesLinea > 0 ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`}>
                        <div className="flex items-center space-x-3">
                            <Zap className={`w-8 h-8 ${resumen.cortesLinea > 0 ? 'text-red-600' : 'text-green-600'}`} />
                            <div>
                                <p className="text-sm text-gray-600">Cortes de Línea</p>
                                <p className="text-2xl font-bold">{resumen.cortesLinea}</p>
                                <p className="text-xs text-gray-500">{formatTiempo(resumen.tiempoTotalLinea)} total</p>
                            </div>
                        </div>
                    </div>

                    <div className={`card ${resumen.microCortes > 0 ? 'bg-orange-50 border-orange-300' : 'bg-green-50 border-green-300'}`}>
                        <div className="flex items-center space-x-3">
                            <Zap className={`w-8 h-8 ${resumen.microCortes > 0 ? 'text-orange-600' : 'text-green-600'}`} />
                            <div>
                                <p className="text-sm text-gray-600">Microcortes</p>
                                <p className="text-2xl font-bold">{resumen.microCortes}</p>
                                <p className="text-xs text-gray-500">Bajones breves</p>
                            </div>
                        </div>
                    </div>

                    <div className={`card ${resumen.cortesComunicacion > 0 ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`}>
                        <div className="flex items-center space-x-3">
                            {resumen.cortesComunicacion > 0 ? (
                                <WifiOff className="w-8 h-8 text-red-600" />
                            ) : (
                                <Wifi className="w-8 h-8 text-green-600" />
                            )}
                            <div>
                                <p className="text-sm text-gray-600">Corte Datos</p>
                                <p className="text-2xl font-bold">{resumen.cortesComunicacion}</p>
                                <p className="text-xs text-gray-500">{formatTiempo(resumen.tiempoTotalComunicacion)} total</p>
                            </div>
                        </div>
                    </div>

                    <div className="card bg-blue-50 border-blue-300">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">%</div>
                            <div>
                                <p className="text-sm text-gray-600">Disponibilidad</p>
                                <p className="text-2xl font-bold text-blue-800">{resumen.disponibilidad}%</p>
                            </div>
                        </div>
                    </div>

                    <div className={`card ${parseFloat(resumen.disponibilidad) >= 99 ? 'bg-green-50' : 'bg-yellow-50'}`}>
                        <div className="flex items-center space-x-3">
                            <AlertTriangle className={`w-8 h-8 ${parseFloat(resumen.disponibilidad) >= 99 ? 'text-green-600' : 'text-yellow-600'}`} />
                            <div>
                                <p className="text-sm text-gray-600">Estado CAMMESA</p>
                                <p className={`text-lg font-bold ${parseFloat(resumen.disponibilidad) >= 99 ? 'text-green-800' : 'text-yellow-800'}`}>
                                    {parseFloat(resumen.disponibilidad) >= 99 ? 'CUMPLE' : 'RIESGO MULTA'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tablas de cortes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Cortes de Línea */}
                <div className="card">
                    <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                        <Zap className="w-5 h-5 text-red-600" />
                        <span>Cortes de Línea Externa</span>
                    </h3>

                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        </div>
                    ) : cortes.linea.length === 0 ? (
                        <div className="text-center py-8 text-green-600">
                            <Zap className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>Sin cortes de línea registrados</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto max-h-64">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">#</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Inicio</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Fin</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Duración</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {cortes.linea.map(c => (
                                        <tr key={c.id} className="bg-red-50">
                                            <td className="px-4 py-2 text-sm">{c.id}</td>
                                            <td className="px-4 py-2 text-sm font-mono">{c.inicio}</td>
                                            <td className="px-4 py-2 text-sm font-mono">{c.fin}</td>
                                            <td className="px-4 py-2 text-sm text-right font-bold text-red-600">{formatTiempo(c.duracion)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Cortes de Comunicación */}
                <div className="card">
                    <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                        <WifiOff className="w-5 h-5 text-red-600" />
                        <span>Cortes de Comunicación (CRÍTICO)</span>
                    </h3>

                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        </div>
                    ) : cortes.comunicacion.length === 0 ? (
                        <div className="text-center py-8 text-green-600">
                            <Wifi className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>Sin cortes de comunicación registrados</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto max-h-64">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">#</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Inicio</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Fin</th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Duración</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {cortes.comunicacion.map(c => (
                                        <tr key={c.id} className="bg-red-100">
                                            <td className="px-4 py-2 text-sm">{c.id}</td>
                                            <td className="px-4 py-2 text-sm font-mono">{c.inicio}</td>
                                            <td className="px-4 py-2 text-sm font-mono">{c.fin}</td>
                                            <td className="px-4 py-2 text-sm text-right font-bold text-red-700">{formatTiempo(c.duracion)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Microcortes */}
            <div className="card">
                <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                    <Zap className="w-5 h-5 text-orange-500" />
                    <span>Microcortes y Bajones de Tensión</span>
                </h3>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                ) : cortes.micro.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>Sin microcortes registrados</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto max-h-64">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">#</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Hora</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Tipo</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Duración</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {cortes.micro.map(c => (
                                    <tr key={c.id} className="bg-orange-50/50">
                                        <td className="px-4 py-2 text-sm">{c.id}</td>
                                        <td className="px-4 py-2 text-sm font-mono">{c.inicio}</td>
                                        <td className="px-4 py-2 text-sm">{c.tipo || 'Corte < 5s'}</td>
                                        <td className="px-4 py-2 text-sm text-right font-bold text-orange-600">{c.duracion > 0 ? `${c.duracion}s` : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default InformeCortes;
